import React, { useMemo } from 'react';
import {
  AlertsTabIcon,
  AviveHeartIcon,
  CallSharedIcon,
  ClockIcon,
  EPChat,
  EPTranscript,
  MapLayerIcon,
  MarkerIcon,
  PhoneIcon,
  UserIcon,
} from '@rsos/assets/icons';
import { CommandHintFooter } from './CommandHintFooter';
import { FIXED_CATEGORY_LABELS } from './GlobalMapSearch.constants';
import styles from './GlobalMapSearch.module.css';
import {
  Dropdown,
  EmptyState,
  ResultContent,
  ResultIcon,
  ResultItem,
  ResultLabel,
  ResultSublabel,
} from './GlobalMapSearch.styles';
import type { SearchResultItem } from './GlobalMapSearch.types';
import { HighlightedRecent } from './RecentSearchesDropdown';
import { buildHighlightRegex, HighlightedText } from './highlightMatch';
import type { RecentSearchEntry } from './recentSearchEntry';
import type { GroupedResults, PreparedItem } from './useGroupedResults';
import {
  LAYER_SUGGESTION_DISMISS_MS,
  type LayerSuggestion,
} from './useLayerSuggestion';

interface MapSearchDropdownProps {
  dropdownRef: React.RefObject<HTMLDivElement>;
  isSearching: boolean;
  query: string;
  results: SearchResultItem[];
  grouped: GroupedResults;
  activeFilter: string | null;
  cursor: number;
  onCursorChange: (idx: number) => void;
  onToggleFilter: (cat: string) => void;
  /**
   * Receives the full `PreparedItem` (not just the underlying
   * `SearchResultItem`) so the parent can capture the row's icon /
   * label / sublabel for the persisted recent-searches surface
   * without having to re-derive them from raw API data.
   */
  onResultClick: (prepared: PreparedItem) => void;
  /**
   * Optional alias-based suggestion to enable an additional layer relevant to
   * the query. When present, a "Turn on <layer>" CTA renders inline with the
   * "Searching" indicator.
   */
  layerSuggestion?: LayerSuggestion | null;
  isActivatingLayer?: boolean;
  onActivateLayer?: () => void;

  onDismissSuggestion?: (layerId: string) => void;

  /**
   * Recent searches that case-insensitively prefix-match the current
   * query — surfaced as a "Recent" section above live results so the
   * user can re-run a previous query (or re-open a previously-clicked
   * record) without finishing typing it. Empty array hides the
   * section entirely (no empty header, no extra divider).
   */
  recentMatches?: RecentSearchEntry[];
  onSelectRecent?: (entry: RecentSearchEntry) => void;
}

const ICON_BY_TYPE: Record<PreparedItem['icon'], React.ReactNode> = {
  axon: <UserIcon width={16} height={16} />,
  place: <MarkerIcon width={16} height={16} />,
  gis: <MapLayerIcon width={16} height={16} />,
  static: <MapLayerIcon width={16} height={16} />,
  dynamic: <MapLayerIcon width={16} height={16} />,
  call: <CallSharedIcon width={16} height={16} />,
  transcript: <EPTranscript width={16} height={16} />,
  message: <EPChat width={16} height={16} />,
  alert: <AlertsTabIcon width={16} height={16} />,
  // RAD-e (MedicAlert profile lookup) — heart glyph reads as the
  // "medical / MedicAlert" cue without needing an org-specific icon.
  'rade-maf': <AviveHeartIcon width={16} height={16} />,
  // RAD-e (ADR phone lookup) — phone glyph mirrors the input shape
  // that triggered the lookup.
  'rade-adr': <PhoneIcon width={16} height={16} />,
};

interface SourceBarProps {
  isSearching: boolean;
  isActivatingLayer?: boolean;
}

const SourceBar: React.FC<SourceBarProps> = ({
  isSearching,
  isActivatingLayer,
}) => {
  const showSearching = isSearching || !!isActivatingLayer;
  if (!showSearching) return null;
  return (
    <div className={styles.sourceBar}>
      <span className={styles.sourceLabel}>
        Searching
        <span className={styles.sourceDots}>
          <span />
          <span />
          <span />
        </span>
      </span>
    </div>
  );
};

interface LayerSuggestionBannerProps {
  layerSuggestion: LayerSuggestion;
  isActivatingLayer?: boolean;
  onActivateLayer?: () => void;
  onDismissSuggestion?: (layerId: string) => void;
  /**
   * Included in the chip's `key` so any keystroke-driven reset is
   * mirrored by the CSS animation restarting from 0%.
   */
  query: string;
}

const FILL_ANIMATION_NAMES = new Set(
  [
    (styles as Record<string, string>).layerSuggestionFill,
    'layerSuggestionFill',
  ].filter((name): name is string => Boolean(name)),
);

const LayerSuggestionBanner: React.FC<LayerSuggestionBannerProps> = ({
  layerSuggestion,
  isActivatingLayer,
  onActivateLayer,
  onDismissSuggestion,
  query,
}) => {
  const idleLabel = layerSuggestion.prompt || `${layerSuggestion.layerName}`;

  const showDepletion = !isActivatingLayer;

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLButtonElement>) => {
    if (!FILL_ANIMATION_NAMES.has(e.animationName)) return;
    onDismissSuggestion?.(layerSuggestion.layer.id);
  };

  return (
    <div className={styles.suggestionFooter}>
      <span className={styles.suggestionLabel}>Suggestion:</span>
      <button
        key={`${layerSuggestion.layer.id}|${query}`}
        type="button"
        className={`${styles.layerSuggestionBtn}${
          showDepletion ? ` ${styles.layerSuggestionBtnDepleting}` : ''
        }`}
        // CSS variable consumed by the fill keyframes — single
        // source of truth for both the visual and the dismissal
        // (which fires on this animation's `end` event).
        style={
          {
            '--layer-suggestion-dismiss-ms': `${LAYER_SUGGESTION_DISMISS_MS}ms`,
          } as React.CSSProperties
        }
        onClick={onActivateLayer}
        onAnimationEnd={handleAnimationEnd}
        disabled={isActivatingLayer || !onActivateLayer}
        title={`Matched: "${layerSuggestion.matchedAlias}"`}
      >
        <span className={styles.layerSuggestionBtnLabel}>
          {isActivatingLayer
            ? `Turning on ${layerSuggestion.layerName}...`
            : `Turn on ${idleLabel}`}
        </span>
      </button>
    </div>
  );
};

const FilterPills: React.FC<{
  categories: string[];
  counts: Record<string, number>;
  activeFilter: string | null;
  onToggle: (cat: string) => void;
}> = ({ categories, counts, activeFilter, onToggle }) => (
  <div className={styles.pillBar}>
    {categories.map(cat => (
      <button
        key={cat}
        type="button"
        className={`${styles.pill}${activeFilter === cat ? ` ${styles.pillActive}` : ''}`}
        onClick={() => onToggle(cat)}
      >
        {FIXED_CATEGORY_LABELS[cat] || cat}
        <span className={styles.pillCount}>{counts[cat]}</span>
      </button>
    ))}
  </div>
);

/**
 * Render the leading icon for a `PreparedItem` row. Falls back to the
 * generic per-type glyph when no category-specific silhouette URL is
 * available. Exported (via the local module) so the inline "Recent"
 * section can render result-backed history entries with the same
 * visual as the live row.
 */
const PreparedIcon: React.FC<{
  icon: PreparedItem['icon'];
  iconUrl?: string;
}> = ({ icon, iconUrl }) => {
  if (iconUrl) {
    // The `mapSearchCategories` SVGs are loaded as URLs, so we
    // can't recolor them via the parent's `currentColor` the way
    // the React-component icons do. Painting them as a CSS mask
    // turns the silhouette into a solid `currentColor` shape
    // again so it picks up the dropdown's primary tint and any
    // future theme tweaks for free.
    return (
      <span
        className={styles.categoryIcon}
        style={
          {
            '--category-icon-url': `url(${iconUrl})`,
          } as React.CSSProperties
        }
        aria-hidden
      />
    );
  }
  return <>{ICON_BY_TYPE[icon]}</>;
};

const ResultRow: React.FC<{
  prepared: PreparedItem;
  isActive: boolean;
  highlightRegex: RegExp | null;
  onClick: () => void;
  onMouseEnter: () => void;
}> = ({ prepared, isActive, highlightRegex, onClick, onMouseEnter }) => {
  const { label, sublabel, distanceLabel, flatIdx, icon, iconUrl } = prepared;
  return (
    <ResultItem
      data-result-index={flatIdx}
      isActive={isActive}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <ResultIcon>
        <PreparedIcon icon={icon} iconUrl={iconUrl} />
      </ResultIcon>
      <ResultContent>
        <ResultLabel>
          <HighlightedText text={label} regex={highlightRegex} />
        </ResultLabel>
        {sublabel && (
          <ResultSublabel>
            <HighlightedText text={sublabel} regex={highlightRegex} />
          </ResultSublabel>
        )}
      </ResultContent>
      {distanceLabel && (
        <span className={styles.distanceBadge}>{distanceLabel}</span>
      )}
    </ResultItem>
  );
};

/**
 * The dropdown surface that hangs off the search bar — renders the in-flight
 * source list, filter pills, grouped results, and empty / loading states.
 * Pure presentational; all behavior is wired via props.
 */
const MapSearchDropdown: React.FC<MapSearchDropdownProps> = ({
  dropdownRef,
  isSearching,
  query,
  results,
  grouped,
  activeFilter,
  cursor,
  onCursorChange,
  onToggleFilter,
  onResultClick,
  layerSuggestion,
  isActivatingLayer,
  onActivateLayer,
  onDismissSuggestion,
  recentMatches,
  onSelectRecent,
}) => {
  const { groups, counts, availableCategories, visibleItems } = grouped;
  const flatCount = visibleItems.length;
  const highlightRegex = useMemo(() => buildHighlightRegex(query), [query]);
  const visibleRecentMatches = recentMatches ?? [];

  return (
    <Dropdown ref={dropdownRef} onMouseDown={e => e.preventDefault()}>
      <SourceBar
        isSearching={isSearching}
        isActivatingLayer={isActivatingLayer}
      />

      {visibleRecentMatches.length > 0 && onSelectRecent && (
        <>
          <div className={styles.sectionHeader}>Recent</div>
          {visibleRecentMatches.map(entry => {
            // Result-backed entries reuse the live row's icon /
            // label / sublabel so the recent surface looks like a
            // continuation of the dropdown's main list. Plain
            // query entries fall back to the generic clock visual.
            const primaryText = entry.result?.label ?? entry.query;
            const sublabel = entry.result?.sublabel;
            return (
              <ResultItem
                key={`recent-${entry.key}`}
                isActive={false}
                onClick={() => onSelectRecent(entry)}
              >
                <ResultIcon>
                  {entry.result ? (
                    <PreparedIcon
                      icon={entry.result.icon}
                      iconUrl={entry.result.iconUrl}
                    />
                  ) : (
                    <ClockIcon width={16} height={16} />
                  )}
                </ResultIcon>
                <ResultContent>
                  <ResultLabel>
                    <HighlightedRecent text={primaryText} query={query} />
                  </ResultLabel>
                  {sublabel && <ResultSublabel>{sublabel}</ResultSublabel>}
                </ResultContent>
              </ResultItem>
            );
          })}
        </>
      )}

      {layerSuggestion && (
        <LayerSuggestionBanner
          layerSuggestion={layerSuggestion}
          isActivatingLayer={isActivatingLayer}
          onActivateLayer={onActivateLayer}
          onDismissSuggestion={onDismissSuggestion}
          query={query}
        />
      )}

      {results.length > 0 && (
        <>
          {availableCategories.length > 1 && (
            <FilterPills
              categories={availableCategories}
              counts={counts}
              activeFilter={activeFilter}
              onToggle={onToggleFilter}
            />
          )}

          {groups.map(group => (
            <React.Fragment key={group.category}>
              {!activeFilter && (
                <div className={styles.sectionHeader}>{group.heading}</div>
              )}
              {group.items.map(prepared => (
                <ResultRow
                  key={prepared.flatIdx}
                  prepared={prepared}
                  isActive={cursor === prepared.flatIdx}
                  highlightRegex={highlightRegex}
                  onClick={() => onResultClick(prepared)}
                  onMouseEnter={() => onCursorChange(prepared.flatIdx)}
                />
              ))}
            </React.Fragment>
          ))}

          {flatCount === 0 && (
            <EmptyState>No results in this category</EmptyState>
          )}
        </>
      )}

      {!isSearching &&
        results.length === 0 &&
        visibleRecentMatches.length === 0 && (
          <>
            <EmptyState>No data found</EmptyState>
            <CommandHintFooter />
          </>
        )}
    </Dropdown>
  );
};

export default MapSearchDropdown;
